import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Checkbox, Input, Modal, Radio, Select, Space, Statistic, message } from 'antd';
import axios from 'axios';
import { API_BASE_URL } from '../../utils/config';

const { TextArea } = Input;

const authHeaders = () => ({
  headers: {
    ...(localStorage.getItem('auth_token') && {
      Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
    }),
  },
});

/**
 * İki modlu modal:
 *  - mod="kabul"  → Görev kabul: hangi adımların gerekli olduğu seçilir,
 *                   toplam termin anlık hesaplanır.
 *  - mod="beklet" → Süreci beklemeye al: sebep + zorunlu açıklama.
 */
const EnerjiGorevKabulModal = ({ open, satir, mod = 'kabul', tanim, onClose, onSuccess }) => {
  const [secili, setSecili] = useState([]);
  const [enerjiTipi, setEnerjiTipi] = useState(null);
  const [sebep, setSebep] = useState(null);
  const [aciklama, setAciklama] = useState('');
  const [kaydediyor, setKaydediyor] = useState(false);
  // 20.08 madde 9: adım anahtarı → firma id
  const [adimFirmalari, setAdimFirmalari] = useState({});
  // 20.08 madde 6: beklemeyi çözecek tek kişi (opsiyonel)
  const [sorumluId, setSorumluId] = useState(null);

  const zorunlular = useMemo(() => tanim?.zorunlu_adimlar || [], [tanim]);

  useEffect(() => {
    if (!open) return;
    setSecili(satir?.secili_adimlar?.length
      ? satir.secili_adimlar
      : (tanim?.varsayilan_secili_adimlar || []));
    setEnerjiTipi(satir?.enerji_tipi || null);
    setSebep(null);
    setAciklama('');
    setSorumluId(null);
    const mevcutFirmalar = {};
    Object.entries(satir?.adim_firmalari || {}).forEach(([adim, f]) => {
      if (f?.firma_id) mevcutFirmalar[adim] = f.firma_id;
    });
    setAdimFirmalari(mevcutFirmalar);
  }, [open, satir, tanim]);

  const firmaSecenekleri = useMemo(
    () => (tanim?.firmalar || []).map((f) => ({ value: f.id, label: f.firma_adi })),
    [tanim]
  );
  const kisiSecenekleri = useMemo(
    () => (tanim?.kisiler || []).map((k) => ({ value: k.kullanici_no, label: k.ad_soyad })),
    [tanim]
  );
  const trFiltre = (input, option) =>
    String(option?.label || '').toLocaleLowerCase('tr').includes(String(input || '').toLocaleLowerCase('tr'));

  // Süzme seçiliyse ara adımlar zaten atlanır; seçim kısıtı bilgi olarak gösterilir.
  const suzme = enerjiTipi === 'suzme';

  const toplamTermin = useMemo(() => {
    if (!tanim?.adimlar) return 0;
    const kapsam = suzme
      ? ['kesif', 'enerji_tipi', tanim.suzme_hedef_adim]
      : secili;
    return tanim.adimlar
      .filter((a) => kapsam.includes(a.anahtar))
      .reduce((t, a) => t + (a.termin_gun || 0), 0);
  }, [tanim, secili, suzme]);

  const kabulEt = async () => {
    if (!secili.length) {
      message.warning('En az bir adım seçin.');
      return;
    }
    setKaydediyor(true);
    try {
      // Süreç kaydı henüz yoksa görev kabulü onu da açar (409 = zaten açılmış).
      if (satir?.kayit_var === false) {
        try {
          await axios.post(`${API_BASE_URL}/enerji/${satir.lokasyon}/baslat`, {}, authHeaders());
        } catch (e) {
          if (e?.response?.status !== 409) throw e;
        }
      }
      const firmalar = {};
      secili.forEach((a) => { if (adimFirmalari[a]) firmalar[a] = adimFirmalari[a]; });
      const res = await axios.post(
        `${API_BASE_URL}/enerji/${satir.lokasyon}/gorev-kabul`,
        { secili_adimlar: secili, enerji_tipi: enerjiTipi || null, adim_firmalari: firmalar },
        authHeaders()
      );
      message.success(`Görev kabul edildi — toplam termin ${res.data?.toplam_termin_gun} gün.`);
      onSuccess && onSuccess();
      onClose && onClose();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Kaydedilemedi.');
    } finally {
      setKaydediyor(false);
    }
  };

  const bekletKaydet = async () => {
    if (!sebep) { message.warning('Bekleme sebebi seçin.'); return; }
    if (!aciklama.trim()) { message.warning('Açıklama zorunludur.'); return; }
    setKaydediyor(true);
    try {
      await axios.post(
        `${API_BASE_URL}/enerji/${satir.lokasyon}/beklet`,
        { sebep, aciklama: aciklama.trim(), sorumlu_id: sorumluId || null },
        authHeaders()
      );
      message.success(sorumluId ? 'Süreç beklemeye alındı; görev atandı.' : 'Süreç beklemeye alındı.');
      onSuccess && onSuccess();
      onClose && onClose();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Kaydedilemedi.');
    } finally {
      setKaydediyor(false);
    }
  };

  const bekletModu = mod === 'beklet';

  return (
    <Modal
      title={`${bekletModu ? 'Beklemeye Al' : 'Görev Kabul'} — ${satir?.lokasyon_adi || satir?.lokasyon || ''}`}
      open={open}
      onCancel={onClose}
      onOk={bekletModu ? bekletKaydet : kabulEt}
      okText={bekletModu ? 'Beklemeye Al' : 'Kabul Et'}
      okButtonProps={{ loading: kaydediyor, danger: bekletModu }}
      cancelText="Vazgeç"
      destroyOnHidden
      width={620}
    >
      {bekletModu ? (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            type="info"
            showIcon
            message="Beklemede geçen süre termin hesabından düşülür."
            description="Süreç bulunduğu adımda duraklatılır; devam edildiğinde kaldığı yerden işler."
          />
          <div>
            <div style={{ fontSize: 12, marginBottom: 4 }}>Bekleme Sebebi <span style={{ color: 'red' }}>*</span></div>
            <Select
              style={{ width: '100%' }}
              placeholder="Sebep seçin"
              value={sebep}
              onChange={setSebep}
              options={(tanim?.bekleme_sebepleri || []).map((b) => ({ value: b.deger, label: b.etiket }))}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, marginBottom: 4 }}>Açıklama <span style={{ color: 'red' }}>*</span></div>
            <TextArea rows={3} value={aciklama} onChange={(e) => setAciklama(e.target.value)} />
          </div>
          <div>
            <div style={{ fontSize: 12, marginBottom: 4 }}>
              Görev atanacak kişi <span style={{ color: '#8c8c8c' }}>(opsiyonel — beklemeyi çözmekle görevlendirilir)</span>
            </div>
            <Select
              style={{ width: '100%' }}
              showSearch
              allowClear
              placeholder="Tek kişi seçin"
              value={sorumluId}
              onChange={(v) => setSorumluId(v || null)}
              options={kisiSecenekleri}
              filterOption={trFiltre}
            />
            <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
              Seçilen kişiye "Bekleme takibi" görevi açılır ve bildirim gider; süreç devam ettirilince görev kapanır.
            </div>
          </div>
        </Space>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            type="info"
            showIcon
            message="Bu lokasyonda hangi adımların yapılacağını seçin."
            description="Toplam termin bu seçime göre hesaplanır ve süreç ekranlarında gösterilir."
          />

          <div>
            <div style={{ fontSize: 12, marginBottom: 4 }}>Enerji Tipi</div>
            <Radio.Group
              value={enerjiTipi}
              onChange={(e) => setEnerjiTipi(e.target.value)}
              options={(tanim?.enerji_tipleri || []).map((t) => ({ value: t.deger, label: t.etiket }))}
              optionType="button"
            />
          </div>

          {suzme && (
            <Alert
              type="warning"
              showIcon
              message="Süzme seçildi — ara adımlar atlanır."
              description="Süreç Keşif → Enerji Tipi → Pano şeklinde işler ve Pano'da biter."
            />
          )}

          <div>
            <div style={{ fontSize: 12, marginBottom: 6 }}>
              Gerekli Adımlar <span style={{ color: '#8c8c8c' }}>— her adım için işi yapacak firma seçilebilir</span>
            </div>
            <Checkbox.Group
              value={secili}
              onChange={setSecili}
              style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}
            >
              {(tanim?.adimlar || []).map((a) => {
                const kapsamda = secili.includes(a.anahtar) && !(suzme && !['kesif', 'enerji_tipi', tanim?.suzme_hedef_adim].includes(a.anahtar));
                return (
                  <div
                    key={a.anahtar}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
                  >
                    <Checkbox
                      value={a.anahtar}
                      disabled={zorunlular.includes(a.anahtar) || suzme}
                      style={{ minWidth: 200 }}
                    >
                      {a.ad}
                      {a.termin_gun > 0 && (
                        <span style={{ color: '#8c8c8c' }}> · {a.termin_gun} gün</span>
                      )}
                      {zorunlular.includes(a.anahtar) && (
                        <span style={{ color: '#8c8c8c' }}> (zorunlu)</span>
                      )}
                    </Checkbox>
                    {!a.karar_adimi && (
                      <Select
                        size="small"
                        style={{ width: 260 }}
                        showSearch
                        allowClear
                        placeholder="Firma (opsiyonel)"
                        disabled={!kapsamda}
                        value={adimFirmalari[a.anahtar] || null}
                        onChange={(v) => setAdimFirmalari((s) => ({ ...s, [a.anahtar]: v || null }))}
                        options={firmaSecenekleri}
                        filterOption={trFiltre}
                      />
                    )}
                  </div>
                );
              })}
            </Checkbox.Group>
          </div>

          <Statistic title="Toplam Termin" value={toplamTermin} suffix="gün" />
        </Space>
      )}
    </Modal>
  );
};

export default EnerjiGorevKabulModal;
